// Labels, colours and small helpers shared by the Service Calls screens.

export const STATUS_META = {
  new:              { label: "New",              cls: "bg-slate-100 text-slate-600" },
  pending_assignment: { label: "New",            cls: "bg-slate-100 text-slate-600" },
  assigned:         { label: "Assigned",         cls: "bg-blue-100 text-blue-700" },
  in_progress:      { label: "In Progress",      cls: "bg-indigo-100 text-indigo-700" },
  escalated:        { label: "Escalated",        cls: "bg-red-100 text-red-700" },
  pending_approval: { label: "Pending Review",   cls: "bg-yellow-100 text-yellow-700" },
  approved:         { label: "Closed",           cls: "bg-green-100 text-green-700" },
  resolved:         { label: "Closed",           cls: "bg-green-100 text-green-700" },
  closed:           { label: "Closed",           cls: "bg-green-100 text-green-700" },
};

export const PRIORITY_META = {
  critical: { label: "Critical", cls: "bg-red-100 text-red-700" },
  high:     { label: "High",     cls: "bg-orange-100 text-orange-700" },
  medium:   { label: "Medium",   cls: "bg-blue-100 text-blue-700" },
  low:      { label: "Low",      cls: "bg-slate-100 text-slate-600" },
};

export const EVENT_LABEL = {
  created: "Raised", assigned: "Assigned", reassigned: "Reassigned", update: "Update",
  submitted: "Report submitted", escalated: "Escalated", returned: "Returned for rework", approved: "Approved and closed",
  sla_warning: "Deadline approaching", sla_breach: "Deadline passed",
};

// Automatic events have no meaningful person behind them.
export const SYSTEM_EVENTS = new Set(["sla_warning", "sla_breach"]);

export const CLOSED = new Set(["closed", "approved", "resolved"]);

// Whoever may raise a call: everyone with an account except the wall-display and kiosk accounts.
export const canRaiseServiceCall = (role) => !!role && !["tv_monitor", "attendance_kiosk"].includes(role);

// True when the call is actually waiting on this person: to be assigned, to be
// reviewed, escalated to them, or theirs to work on and report. `actions` also
// carries things anyone with access can always do (admin edit, photos,
// reassigning a working call), which must not count or an admin would see
// every call as "needs my action". Mirrors serviceNeedsMyAction on mobile.
export const needsMyAction = (call) => {
  const actions = call.actions || [];
  if (actions.includes("assign") || actions.includes("approve") || actions.includes("submit")) return true;
  return call.status === "escalated" && actions.includes("reassign");
};

// Is this person one of the people the call is assigned to? Reads the full team
// when the server sends it, else the single assigned_to (older responses).
export const isAssignedTo = (call, userId) => {
  if (!userId) return false;
  if (Array.isArray(call.assignees) && call.assignees.length) return call.assignees.some((person) => person.id === userId);
  return call.assigned_to === userId;
};

export const statusMeta = (status) => STATUS_META[status] || { label: status, cls: "bg-slate-100 text-slate-600" };
export const priorityMeta = (priority) => PRIORITY_META[priority] || { label: priority, cls: "bg-slate-100 text-slate-600" };

export function formatWhen(value) {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

// The one line that tells a person what happens next with this call.
export function nextStepText(call) {
  const actions = call.actions || [];
  if (actions.includes("assign")) return "Choose who should handle this call.";
  if (actions.includes("submit")) return "Post updates as the work goes on. When it is finished, submit your report, or escalate if you are stuck.";
  if (actions.includes("approve")) return "The assignee submitted a report. Review it, then approve to close the call or return it with a reason.";
  if (actions.includes("reassign") && call.status === "escalated") return `Escalated${call.escalation_reason ? `: ${call.escalation_reason}` : ""}. Reassign it to someone else.`;
  switch (call.status) {
    case "new": case "pending_assignment": return "Waiting to be assigned.";
    case "assigned": case "in_progress": return `${call.assigned_to_name || "The assignee"} is working on this.`;
    case "escalated": return "Escalated to the admins, who will reassign it.";
    case "pending_approval": return "Report submitted. Waiting for review.";
    default: return "This call is closed.";
  }
}
